import React, { useEffect } from 'react';
import PropTypes from 'prop-types';
import { Layout, Section, Grid, GridItem, Stack, Container } from '@fiverr-private/layout_components';
import { getContext } from '@fiverr-private/fiverr_context';
import { withErrorBoundary } from '@fiverr-private/ui_utils';
import { DEFAULT_FIVERR_LOCALE } from '@fiverr-private/localization';
import { logger } from '@fiverr-private/obs';
import { SkillCategoryPageProps } from '../../../shared/types';
import { sendBqEvent } from '../../../events/bigQuery';
import { RatingsBanner } from '../RatingsBanner';
import { ProHeroBanner } from '../ProHeroBanner';
import { HeroBanner } from '../HeroBanner';
import { SimilarServices } from '../SimilarServices';
import { ViewMoreLink } from '../ViewMoreLink';
import { FoundExperts } from '../FoundExperts/FoundExperts';
import { BrandsBanner } from '../BrandsBanner/BrandsBanner';
import { ContactSellersBanner } from '../ContactSellersBanner';
import { HowToHire } from '../HowToHire';
import { ExpertCard } from '../ExpertCard';
import {
    getContent,
    getRelatedSkills,
    getHeroBannerDescription,
    getHeroBannerTitle,
    sanitizeHTML,
} from '../../../utils';
import { cardContainer } from './styles.ve.css';

const SkillCategoryPage = (props) => {
    const {
        skill,
        enrichedExperts,
        clientsRate,
        relatedExpertises,
        expertsFoundTotal,
        builderData,
        currency,
        experts,
    } = props as SkillCategoryPageProps;
    const { url: hostUrl, locale } = getContext();

    const isDefaultLocale = locale === DEFAULT_FIVERR_LOCALE;

    const onExpertCardError = (message: string) =>
        logger.error(message, {
            go_searcher_experts: experts?.map((expert) => {
                const { id, uuid, position, type } = expert;
                return {
                    expert: JSON.stringify({
                        id,
                        uuid,
                        position,
                        type,
                    }),
                };
            }),
            enriched_experts: enrichedExperts.map((expert) => {
                const {
                    seller: { id, displayName, username },
                } = expert;
                return {
                    seller: JSON.stringify({
                        id,
                        displayName,
                        username,
                    }),
                };
            }),
        });

    useEffect(() => {
        sendBqEvent('page_view', {
            type: 'skills.show',
            actionType: 'page_view',
            element: {
                type: skill.isProSkill ? 'business' : 'mp',
                text: '',
                name: skill.slug,
            },
        });
    }, [skill]);

    const heroBannerTitle = getHeroBannerTitle({ builderData, skill, count: enrichedExperts.length });
    const heroBannerDescription = getHeroBannerDescription({ builderData, skill, count: enrichedExperts.length });

    const relatedSkills = getRelatedSkills({ builderData });
    const content = getContent({ builderData, skill, count: enrichedExperts.length });
    const contentExists = sanitizeHTML(content).length > 0;

    return (
        <Layout>
            {skill.isProSkill ? (
                <ProHeroBanner
                    hostUrl={hostUrl}
                    isDefaultLocale={isDefaultLocale}
                    professionAliasPlural={skill.translated?.professionAliasPlural || ''}
                    category={skill.name || ''}
                    title={heroBannerTitle}
                    description={heroBannerDescription}
                    slug={skill.slug}
                />
            ) : (
                <HeroBanner
                    isDefaultLocale={isDefaultLocale}
                    professionAliasPlural={skill.translated?.professionAliasPlural || ''}
                    title={heroBannerTitle}
                    description={heroBannerDescription}
                />
            )}
            <Section paddingTop="0" paddingBottom="0">
                <Stack direction="column" gap="0">
                    {clientsRate.score > 0 ? (
                        <RatingsBanner
                            isDefaultLocale={isDefaultLocale}
                            professionAliasPlural={skill.translated.professionAliasPlural || ''}
                            category={skill.translated?.name}
                            numberOfReviews={clientsRate.numberOfReviews}
                            score={clientsRate.score}
                        />
                    ) : (
                        <Container marginTop={{ default: '8', sm: '8', md: '8', lg: '14', xl: '14' }} />
                    )}
                    {!!expertsFoundTotal && (
                        <FoundExperts expertsFound={enrichedExperts.length} total={expertsFoundTotal} />
                    )}
                    <Grid
                        templateColumns={{
                            default: '100%',
                            sm: '100%',
                            md: '50%_50%',
                            lg: '33%_33%_33%',
                            xl: '33%_33%_33%',
                        }}
                        gap="6"
                    >
                        {enrichedExperts?.map((expert) => (
                            <GridItem className={cardContainer} key={expert.seller.id}>
                                <ExpertCard
                                    skill={skill}
                                    expert={expert}
                                    currency={currency}
                                    onError={onExpertCardError}
                                />
                            </GridItem>
                        ))}
                    </Grid>
                    {skill.isProSkill && <ViewMoreLink slug={skill.slug} category={clientsRate.category} />}
                </Stack>
            </Section>
            {relatedExpertises?.length > 0 && (
                <SimilarServices
                    slug={skill.slug}
                    isPro={skill.isProSkill}
                    category={skill.translated.name || ''}
                    expertises={relatedExpertises}
                    relatedSkills={relatedSkills}
                />
            )}
            <Section paddingTop="0" paddingBottom="0">
                <BrandsBanner />
            </Section>
            {contentExists && (
                <Section>
                    <HowToHire content={content} />
                </Section>
            )}
            {skill.isProSkill && <ContactSellersBanner />}
        </Layout>
    );
};

SkillCategoryPage.propTypes = {
    clientsRate: PropTypes.shape({
        category: PropTypes.string.isRequired,
        score: PropTypes.number.isRequired,
        numberOfReviews: PropTypes.number.isRequired,
        highestScore: PropTypes.number,
    }).isRequired,
    skill: PropTypes.shape({
        isProSkill: PropTypes.bool.isRequired,
        id: PropTypes.string.isRequired,
        classification: PropTypes.string,
        slug: PropTypes.string,
        name: PropTypes.string,
        translated: PropTypes.shape({
            name: PropTypes.string.isRequired,
            metaTitle: PropTypes.string.isRequired,
            metaDescription: PropTypes.string.isRequired,
            title: PropTypes.string.isRequired,
            description: PropTypes.string.isRequired,
            content: PropTypes.string,
            professionAliasPlural: PropTypes.string,
            professionAlias: PropTypes.string,
        }).isRequired,
    }).isRequired,
    enrichedExperts: PropTypes.arrayOf(PropTypes.object).isRequired,
    relatedExpertises: PropTypes.arrayOf(PropTypes.object).isRequired,
    expertsFoundTotal: PropTypes.number.isRequired,
    builderData: PropTypes.shape({
        skillTemplateData: PropTypes.shape({
            description: PropTypes.string.isRequired,
            h1: PropTypes.string.isRequired,
            tagline: PropTypes.string.isRequired,
            title: PropTypes.string.isRequired,
            content: PropTypes.string.isRequired,
            relatedSkills: PropTypes.arrayOf(PropTypes.string),
        }),
        genericTemplateData: PropTypes.shape({
            description: PropTypes.string.isRequired,
            h1: PropTypes.string.isRequired,
            tagline: PropTypes.string.isRequired,
            title: PropTypes.string.isRequired,
        }),
    }).isRequired,
    currency: PropTypes.object,
    experts: PropTypes.shape({
        id: PropTypes.string,
        uuid: PropTypes.string,
        position: PropTypes.number,
        gigs: PropTypes.arrayOf(PropTypes.object),
        expertises: PropTypes.arrayOf(PropTypes.object),
        type: PropTypes.string,
        map: PropTypes.object,
    }),
};

export default withErrorBoundary(SkillCategoryPage);
